import * as yup from "yup";
import {
  cardDesignInitialValues,
  createValidationSchema,
  validate,
  validateIfFile,
} from "@";
import { ProjectFormValues, ProjectType } from "../types";

const projectLicenceCodeSchema = yup.object().shape({
  code: yup.string().trim().required("رقم الترخيص مطلوب"),
  startDate: yup.string().required("تاريخ بداية الترخيص مطلوب"),
  endDate: yup
    .string()
    .required("تاريخ نهاية الترخيص مطلوب")
    .test(
      "end-after-start",
      "تاريخ نهاية الترخيص يجب أن يكون بعد تاريخ البداية",
      function (value) {
        const { startDate } = this.parent;
        if (!value || !startDate) return true;
        return new Date(value) >= new Date(startDate);
      },
    ),
});

const baseAttributeSchema = yup.object().shape({
  title: validate("req"),
  price: validate("numberWithValue"),
});

const deductionAttributeSchema = baseAttributeSchema.shape({
  duration: yup.string().required("فترة الاستقطاع مطلوبة"),
  endingTime: yup.string().required("نهاية فترة الاستقطاع مطلوبة"),
});

const mealAttributeSchema = baseAttributeSchema.shape({
  numberOfBeneficiariesNames: yup
    .number()
    .min(1, "يجب أن يكون عدد الملصقات 1 أو أكثر")
    .max(1000, "يجب أن يكون عدد الملصقات 1000 أو أقل")
    .required("عدد الملصقات مطلوب"),
});

const verificationDateFields = {
  verificationStartDate: yup.string().nullable(),
  verificationEndDate: yup
    .string()
    .nullable()
    .test(
      "end-after-start",
      "تاريخ نهاية التوثيق يجب أن يكون بعد تاريخ البداية",
      function (value) {
        const { verificationStartDate } = this.parent;
        if (!value || !verificationStartDate) return true;
        return new Date(value) >= new Date(verificationStartDate);
      },
    ),
};

const verificationLimitationSchema = yup.object().shape({
  verificationStageName: yup.string().required("اسم المرحلة مطلوب"),
  smsMessage: yup.string().required("رسالة SMS مطلوبة"),
  verificationStageNumber: yup.number().min(1).required(),
  minNumberOfPhotos: yup
    .number()
    .min(0, "يجب أن يكون الحد الأدنى للصور 0 أو أكثر")
    .required("الحد الأدنى للصور مطلوب"),
  maxNumberOfPhotos: yup
    .number()
    .min(0, "يجب أن يكون الحد الأقصى للصور 0 أو أكثر")
    .test(
      "min-max-photos",
      "يجب أن يكون الحد الأقصى للصور أكبر من أو يساوي الحد الأدنى",
      function (value) {
        const { minNumberOfPhotos } = this.parent;
        return value === undefined || value >= (minNumberOfPhotos || 0);
      },
    )
    .required("الحد الأقصى للصور مطلوب"),
  minNumberOfVideos: yup
    .number()
    .min(0, "يجب أن يكون الحد الأدنى للفيديوهات 0 أو أكثر")
    .required("الحد الأدنى للفيديوهات مطلوب"),
  maxNumberOfVideos: yup
    .number()
    .min(0, "يجب أن يكون الحد الأقصى للفيديوهات 0 أو أكثر")
    .test(
      "min-max-videos",
      "يجب أن يكون الحد الأقصى للفيديوهات أكبر من أو يساوي الحد الأدنى",
      function (value) {
        const { minNumberOfVideos } = this.parent;
        return value === undefined || value >= (minNumberOfVideos || 0);
      },
    )
    .required("الحد الأقصى للفيديوهات مطلوب"),
  maxPhotoSize: yup
    .number()
    .min(1, "يجب أن يكون حجم الصورة 1 ميجابايت على الأقل")
    .required("الحد الأقصى لحجم الصورة مطلوب"),
  maxVideoSize: yup
    .number()
    .min(1, "يجب أن يكون حجم الفيديو 1 ميجابايت على الأقل")
    .required("الحد الأقصى لحجم الفيديو مطلوب"),
});

export const schema = createValidationSchema({
  // Basic Information
  name: validate("req"),
  title: validate("req"),
  order: validate("number"),
  summary: validate("req"),
  image: validateIfFile("image", "imageUrl"),
  projectLicence: validateIfFile("nullable-image", "projectLicenceUrl"),
  projectType: validate("req"),
  haveCertificate: yup.boolean().default(false),
  notifyWhenCompleted: yup.boolean().default(false),
  showForFastDonation: yup.boolean().default(false),
  classificationIds: validate("array"),
  projectLicenceCodes: yup.array().of(projectLicenceCodeSchema).nullable(),

  // Certificate
  certificate: yup.string().when(["haveCertificate", "certificateUrl"], {
    is: (haveCertificate: boolean, certificateUrl: string) =>
      haveCertificate && !certificateUrl,
    then: () => validateIfFile("nullable-image", "certificateUrl"),
    otherwise: () => yup.mixed().nullable(),
  }),

  // Project Attributes
  projectAttributes: yup.lazy((_value, { parent }) => {
    const projectType = Number(parent?.projectType);
    const hasVerification = !!parent?.enableVerification;

    if (projectType === ProjectType.Open) return yup.array().nullable();

    let itemSchema = baseAttributeSchema;
    if (projectType === ProjectType.Deduction)
      itemSchema = deductionAttributeSchema;
    else if (projectType === ProjectType.Meal) itemSchema = mealAttributeSchema;

    if (
      hasVerification &&
      [ProjectType.Meal, ProjectType.Grades].includes(projectType)
    ) {
      itemSchema = itemSchema.shape(verificationDateFields);
    }

    return yup
      .array()
      .of(itemSchema)
      .min(1, "يجب إضافة خيار تبرع واحد على الأقل");
  }),

  minimumOpenDonationAmount: yup
    .array()
    .when(["allowOpenDonation", "projectType"], {
      is: (allowOpenDonation: boolean, projectType: string | number) =>
        projectType == ProjectType.Open ||
        (allowOpenDonation && projectType == ProjectType.Grades),
      then: () => validate("numberWithValue"),
      otherwise: () => yup.string().nullable(),
    }),

  // Progress Bar
  allRequiredAmount: yup.array().when(["showProgressBar"], {
    is: true,
    then: () => validate("numberWithValue"),
    otherwise: () => yup.string().nullable().label("allRequiredAmount"),
  }),

  // Verification Limitations
  projectVerificationLimitations: yup.array().when(["enableVerification"], {
    is: true,
    then: () =>
      yup
        .array()
        .of(verificationLimitationSchema)
        .min(1, "يجب إضافة مرحلة توثيق واحدة على الأقل"),
    otherwise: () => yup.array().nullable(),
  }),

  // currentPaidAmount
  currentPaidAmount: validate("numberWithValue").nullable(),
});

export const defaultValues: Partial<ProjectFormValues> = {
  // Basic Information
  order: 0,
  image: null,
  projectDescriptionImages: null,
  enableVerification: false,
  projectType: ProjectType.Open,
  projectLicence: null,
  bankAccountsIds: [],
  certificateUrl: null,
  projectVerificationLimitations: [],
  projectLicenceCodes: [],
  classificationIds: [],
  certificateView: null,
  projectAttributes: [],
  minimumOpenDonationAmount: 0,
  projectAccountShowingOption: 0,
  customerSuggestedCampaignId: undefined,
  adahiProductId: undefined,
  adahiRitualId: undefined,
  endowmentCertificateDesign: [cardDesignInitialValues],
  themeId: undefined,
};
